import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Search,
  Plus,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  Filter,
  Calendar,
  User,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  getAllBlogs,
  updateBlog,
  deleteBlog,
  selectAdminBlogs,
  selectAdminBlogsLoading,
  selectAdminBlogsError,
  selectFilteredAdminBlogs,
  selectPaginatedAdminBlogs,
  selectAdminBlogsTotalPages,
  selectBlogFilters,
  selectBlogPagination,
  setStatusFilter,
  setCategoryFilter,
  setSearchTerm,
  setCurrentPage,
  setItemsPerPage,
} from "@/store/slices/blogSlice";
import Loader from "@/components/loader/loader";

const statusColors = {
  published: "bg-green-100 text-green-800",
  draft: "bg-yellow-100 text-yellow-800",
  scheduled: "bg-blue-100 text-blue-800",
  archived: "bg-gray-100 text-gray-800",
};

export default function BlogList() {
  const dispatch = useAppDispatch();
  const blogs = useAppSelector(selectAdminBlogs);
  const loading = useAppSelector(selectAdminBlogsLoading);
  const error = useAppSelector(selectAdminBlogsError);
  const filters = useAppSelector(selectBlogFilters);
  const pagination = useAppSelector(selectBlogPagination);
  const paginatedBlogs = useAppSelector(selectPaginatedAdminBlogs);
  const totalPages = useAppSelector(selectAdminBlogsTotalPages);
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [deletingBlog, setDeletingBlog] = useState(null);
  const [blogToDelete, setBlogToDelete] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const blogsPerPage = 10;

  // Fetch blogs from API
  useEffect(() => {
    dispatch(getAllBlogs());
    dispatch(setItemsPerPage(blogsPerPage));
  }, [dispatch]);

  // Get unique categories for filter
  const categories = useMemo(() => {
    return [...new Set(blogs.map((blog) => blog.category).filter(Boolean))];
  }, [blogs]);

  const handleDeleteClick = (blogId) => {
    setBlogToDelete(blogId);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!blogToDelete) return;

    setDeletingBlog(blogToDelete);
    setIsDeleteDialogOpen(false);
    
    try {
      await dispatch(deleteBlog(blogToDelete)).unwrap();
      // Blog will be removed from Redux state automatically
    } catch (err) {
      console.error("Error deleting blog:", err);
      // Error already handled in thunk
    } finally {
      setDeletingBlog(null);
      setBlogToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
    setBlogToDelete(null);
  };

  const handleStatusChange = async (blogId, newStatus) => {
    setUpdatingStatus({ ...updatingStatus, [blogId]: true });
    try {
      const blog = blogs.find((b) => b.id === blogId);
      const blogData = {
        status: newStatus,
      };

      // If status is scheduled, ensure scheduleDate exists
      if (newStatus === "scheduled") {
        // If blog already has a scheduleDate, preserve it
        if (blog?.scheduleDate) {
          blogData.scheduleDate = blog.scheduleDate;
        } else {
          // If no scheduleDate exists, set it to a future date (7 days from now)
          const futureDate = new Date();
          futureDate.setDate(futureDate.getDate() + 7);
          blogData.scheduleDate = futureDate.toISOString();
        }
      }

      await dispatch(updateBlog({ blogId, blogData })).unwrap();
      // Blog will be updated in Redux state automatically
    } catch (err) {
      console.error("Error updating blog status:", err);
      // Error already handled in thunk
    } finally {
      setUpdatingStatus({ ...updatingStatus, [blogId]: false });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "Invalid Date";
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="w-full max-w-full overflow-hidden flex items-center justify-center min-h-[400px]">
        <Loader />
      </div>
    );
  }

  // Error state
  if (error && blogs.length === 0) {
    return (
      <div className="w-full max-w-full overflow-hidden">
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center gap-4 py-8">
              <AlertCircle className="h-12 w-12 text-destructive" />
              <div className="text-center">
                <h3 className="text-lg font-semibold mb-2">Error Loading Blogs</h3>
                <p className="text-muted-foreground mb-4">{error}</p>
                <Button onClick={() => dispatch(getAllBlogs())}>Try Again</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 max-w-full overflow-x-hidden">
      <div className="space-y-6 min-w-0 max-w-full">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold truncate">
              Blog Posts
            </h1>
            <p className="text-muted-foreground text-sm sm:text-base">
              Manage your blog content
            </p>
          </div>
          <div className="flex-shrink-0 w-full sm:w-auto">
            <Link to="/admin/blog/create">
              <Button className="w-full sm:w-auto">
                <Plus className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Create New Post</span>
                <span className="sm:hidden">New Post</span>
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Posts</CardTitle>
              <Eye className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{blogs.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Published</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {blogs.filter((blog) => blog.status === "published").length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Drafts</CardTitle>
              <Edit className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {blogs.filter((blog) => blog.status === "draft").length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Views</CardTitle>
              <User className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {blogs
                  .reduce((total, blog) => total + (blog.views || 0), 0)
                  .toLocaleString()}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters and Search */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="w-full">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search by title or author..."
                    value={filters.searchTerm || ""}
                    onChange={(e) => dispatch(setSearchTerm(e.target.value))}
                    className="pl-10 w-full"
                  />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Select 
                  value={filters.status || "all"} 
                  onValueChange={(value) => dispatch(setStatusFilter(value === "all" ? "" : value))}
                >
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={filters.category || "all"}
                  onValueChange={(value) => dispatch(setCategoryFilter(value === "all" ? "" : value))}
                >
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue placeholder="Filter by category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Blog Posts - Mobile/Tablet Card View */}
        <div className="xl:hidden space-y-4">
          {paginatedBlogs.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No blogs found.</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            paginatedBlogs.map((blog) => (
              <Card key={blog.id}>
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <img
                      src={blog.featuredImage || "/placeholder.svg"}
                      alt={blog.title}
                      className="w-16 h-16 sm:w-20 sm:h-20 rounded object-cover flex-shrink-0"
                      onError={(e) => {
                        e.target.src = "/placeholder.svg";
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-sm sm:text-base line-clamp-2">
                            {blog.title}
                          </h3>
                          <p className="text-xs sm:text-sm text-muted-foreground truncate">
                            /{blog.slug}
                          </p>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              className="h-8 w-8 p-0 flex-shrink-0"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <Link to={`/blog/${blog.slug}`} target="_blank">
                                <Eye className="mr-2 h-4 w-4" />
                                View Post
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                              <Link to={`/admin/blog/edit/${blog.slug}`}>
                                <Edit className="mr-2 h-4 w-4" />
                                Edit
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDeleteClick(blog.id)}
                              className="text-red-600"
                              disabled={deletingBlog === blog.id}
                            >
                              {deletingBlog === blog.id ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Deleting...
                                </>
                              ) : (
                                <>
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete
                                </>
                              )}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        {blog.category && (
                          <Badge variant="outline" className="text-xs">
                            {blog.category}
                          </Badge>
                        )}
                        <Select
                          value={blog.status}
                          onValueChange={(value) =>
                            handleStatusChange(blog.id, value)
                          }
                          disabled={updatingStatus[blog.id]}
                        >
                          <SelectTrigger className="w-[100px] h-7">
                            <SelectValue>
                              {updatingStatus[blog.id] ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Badge
                                  className={`${statusColors[blog.status] || statusColors.draft} text-xs`}
                                >
                                  {blog.status}
                                </Badge>
                              )}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="published">Published</SelectItem>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="scheduled">Scheduled</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-xs text-muted-foreground">
                        <div>
                          <span className="font-medium">Author:</span>
                          <div className="truncate">{blog.author || "Admin"}</div>
                        </div>
                        <div>
                          <span className="font-medium">Date:</span>
                          <div>{formatDate(blog.publishDate)}</div>
                        </div>
                        <div>
                          <span className="font-medium">Views:</span>
                          <div>{(blog.views || 0).toLocaleString()}</div>
                        </div>
                        <div>
                          <span className="font-medium">Comments:</span>
                          <div>{blog.comments || 0}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Blog Posts - Desktop Table View */}
        <div className="hidden xl:block w-full" style={{ maxWidth: '100%' }}>
          <Card className="w-full" style={{ maxWidth: '100%' }}>
            <CardContent className="p-0 w-full" style={{ maxWidth: '100%' }}>
              <div 
                className="w-full overflow-x-auto overflow-y-auto max-h-[600px] [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-track]:bg-transparent"
                style={{ maxWidth: '100%' }}
              >
                <table className="min-w-[1000px]">
                  <thead className="sticky top-0 z-10 bg-card border-b shadow-sm">
                    <tr>
                      <th className="text-left p-4 font-medium bg-card whitespace-nowrap">Title</th>
                      <th className="text-left p-4 font-medium bg-card whitespace-nowrap">Category</th>
                      <th className="text-left p-4 font-medium bg-card whitespace-nowrap">Status</th>
                      <th className="text-left p-4 font-medium bg-card whitespace-nowrap">Author</th>
                      <th className="text-left p-4 font-medium bg-card whitespace-nowrap">Date</th>
                      <th className="text-left p-4 font-medium bg-card whitespace-nowrap">Views</th>
                      <th className="text-left p-4 font-medium bg-card whitespace-nowrap">Comments</th>
                      <th className="text-right p-4 font-medium bg-card whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedBlogs.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="p-8 text-center text-muted-foreground">
                          No blogs found.
                        </td>
                      </tr>
                    ) : (
                      paginatedBlogs.map((blog) => (
                        <tr key={blog.id} className="border-b hover:bg-muted/50 transition-colors">
                          <td className="p-4">
                            <div className="flex items-center gap-3 min-w-[250px]">
                              <img
                                src={blog.featuredImage || "/placeholder.svg"}
                                alt={blog.title}
                                className="w-12 h-12 rounded object-cover flex-shrink-0"
                                onError={(e) => {
                                  e.target.src = "/placeholder.svg";
                                }}
                              />
                              <div className="min-w-0 flex-1">
                                <div className="font-medium truncate">
                                  {blog.title}
                                </div>
                                <div className="text-sm text-muted-foreground truncate">
                                  /{blog.slug}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="p-4 whitespace-nowrap">
                            {blog.category && (
                              <Badge variant="outline">{blog.category}</Badge>
                            )}
                          </td>
                          <td className="p-4 whitespace-nowrap">
                            <Select
                              value={blog.status}
                              onValueChange={(value) =>
                                handleStatusChange(blog.id, value)
                              }
                              disabled={updatingStatus[blog.id]}
                            >
                              <SelectTrigger className="w-[120px] h-8">
                                <SelectValue>
                                  {updatingStatus[blog.id] ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <Badge
                                      className={`${statusColors[blog.status] || statusColors.draft} text-xs`}
                                    >
                                      {blog.status}
                                    </Badge>
                                  )}
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="published">Published</SelectItem>
                                <SelectItem value="draft">Draft</SelectItem>
                                <SelectItem value="scheduled">Scheduled</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-4 text-sm whitespace-nowrap">{blog.author || "Admin"}</td>
                          <td className="p-4 text-sm whitespace-nowrap">{formatDate(blog.publishDate)}</td>
                          <td className="p-4 text-sm whitespace-nowrap">{(blog.views || 0).toLocaleString()}</td>
                          <td className="p-4 text-sm whitespace-nowrap">{blog.comments || 0}</td>
                          <td className="p-4 whitespace-nowrap">
                            <div className="flex items-center justify-end gap-2">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" className="h-8 w-8 p-0">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem asChild>
                                    <Link to={`/blog/${blog.slug}`} target="_blank">
                                      <Eye className="mr-2 h-4 w-4" />
                                      View Post
                                    </Link>
                                  </DropdownMenuItem>
                                  <DropdownMenuItem asChild>
                                    <Link to={`/admin/blog/edit/${blog.slug}`}>
                                      <Edit className="mr-2 h-4 w-4" />
                                      Edit
                                    </Link>
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleDeleteClick(blog.id)}
                                    className="text-red-600"
                                    disabled={deletingBlog === blog.id}
                                  >
                                    {deletingBlog === blog.id ? (
                                      <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Deleting...
                                      </>
                                    ) : (
                                      <>
                                        <Trash2 className="mr-2 h-4 w-4" />
                                        Delete
                                      </>
                                    )}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-muted-foreground">
              Showing {(pagination.currentPage - 1) * pagination.itemsPerPage + 1} to{" "}
              {Math.min(pagination.currentPage * pagination.itemsPerPage, paginatedBlogs.length + (pagination.currentPage - 1) * pagination.itemsPerPage)} of{" "}
              {blogs.length} posts
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => dispatch(setCurrentPage(Math.max(pagination.currentPage - 1, 1)))}
                disabled={pagination.currentPage === 1}
              >
                Previous
              </Button>
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let page;
                  if (totalPages <= 5) {
                    page = i + 1;
                  } else if (pagination.currentPage <= 3) {
                    page = i + 1;
                  } else if (pagination.currentPage >= totalPages - 2) {
                    page = totalPages - 4 + i;
                  } else {
                    page = pagination.currentPage - 2 + i;
                  }
                  return (
                    <Button
                      key={page}
                      variant={pagination.currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => dispatch(setCurrentPage(page))}
                      className="w-8 h-8 p-0"
                    >
                      {page}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  dispatch(setCurrentPage(Math.min(pagination.currentPage + 1, totalPages)))
                }
                disabled={pagination.currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog 
        open={isDeleteDialogOpen} 
        onOpenChange={(open) => {
          if (!open) {
            handleDeleteCancel();
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Blog Post</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this blog post? This action cannot be undone and will permanently delete the blog post and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDeleteCancel} disabled={deletingBlog !== null}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700 text-white focus:ring-red-600"
              disabled={deletingBlog !== null}
            >
              {deletingBlog ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
